import { getWorkspaceInput } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { Goto } from "@/shared/navigation";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError, toolSuccess } from "../ClientTools";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface OpenPanelInput {
  message_path_id: string;
  /** Timestamp in nanoseconds since Unix epoch. */
  timestamp?: number;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: OpenPanelInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  if (!deps.topicService) {
    return toolError("Topic service is not available");
  }

  if (!input.message_path_id) {
    return toolError("message_path_id is required");
  }

  try {
    const topicService = deps.topicService;
    const messagePath = await topicService.getMessagePathById(
      input.message_path_id,
    );
    const topic = await topicService.getTopicById(messagePath.topic_id);
    const panelType = determineDefaultPanelType(
      messagePath.canonical_data_type,
    );
    const timestamp =
      input.timestamp !== undefined ? BigInt(input.timestamp) : undefined;

    if (deps.isOnVisualizerPage?.()) {
      const published = deps.publishWorkspaceAction?.({
        type: "open_message_path",
        payload: { messagePath, topic, timestamp },
      });
      if (!published) {
        return toolError("Failed to publish workspace action");
      }
      return toolSuccess("open_panel", { opened_in: "current_visualizer" });
    }

    const layout = getWorkspaceInput(panelType, topic, messagePath);
    deps.goto.visualize({
      dataSource: { files: [topic.association.association_id] },
      params: { layout, t: timestamp },
    });

    return toolSuccess("open_panel", {
      navigated_to: Goto.route.Visualize,
    });
  } catch (err) {
    return toolError(err instanceof Error ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<OpenPanelInput>({
  name: "visualize_open_panel",
  description: `Open a message path in a new visualizer panel (plot, image, log, etc. based on data type). If the user is not already on the visualizer page, navigates there first. Optionally seeks the timeline to a specific timestamp after opening.`,
  input_schema: {
    type: "object",
    properties: {
      message_path_id: {
        type: "string",
        description:
          "Message path ID (e.g., 'mp_abc123'). Identifies the specific data stream to visualize.",
      },
      timestamp: {
        type: "integer",
        description:
          "Timestamp in nanoseconds since Unix epoch. Optional — seeks the timer after opening.",
      },
    },
    required: ["message_path_id"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open Panel",
  displayDescription: "Opens a data panel in the visualizer.",
});
